/**
 * The completed paper, with what the student chose beside what was correct.
 *
 * The stored answer for a question is not its text: a question whose options
 * live with it records a choice by position, while a word bank question records
 * the text itself. Both arrive here as a bare value, so the work in this module
 * is turning those values back into the options a student would recognise.
 */

export interface ReviewOption {
  answer: string;
  answerContentType?: string;
  answerImage?: string;
  value: string;
}

export interface ReviewQuestion {
  questionNumber: number;
  questionId: string;
  marks: number;
  marksAwarded: number;
  answered: boolean;
  selectedAnswer: string[];
  correctAnswer: string[];
  isCorrect: boolean;
  question: {
    name?: string;
    questionType?: string;
    questionContentType?: string;
    questionHead?: string;
    question?: string;
    questionImage?: string;
    answer?: ReviewOption[];
  };
}

/** How one option relates to this student's answer. */
export type OptionState = 'chosen-correct' | 'chosen-wrong' | 'correct' | 'plain';

const first = (values: string[] | undefined): string => `${values?.[0] ?? ''}`;

/** The option a stored value refers to, or nothing if it refers to none. */
export const optionForValue = (
  options: ReviewOption[] | undefined,
  value: string,
): ReviewOption | undefined =>
  (options ?? []).find((option) => `${option.value}` === `${value}`);

/**
 * What an option should be shown as.
 *
 * The correct option is always marked, whether or not the student found it, so
 * a paper is worth reading back even where every answer was wrong.
 */
export const optionState = (
  option: ReviewOption,
  question: ReviewQuestion,
): OptionState => {
  const chosen = `${option.value}` === first(question.selectedAnswer) && question.answered;
  const correct = `${option.value}` === first(question.correctAnswer);

  if (chosen && correct) {
    return 'chosen-correct';
  }
  if (chosen) {
    return 'chosen-wrong';
  }
  return correct ? 'correct' : 'plain';
};

/** The one-word verdict on a question. */
export const verdict = (question: ReviewQuestion): 'Correct' | 'Incorrect' | 'Not answered' => {
  if (!question.answered) {
    return 'Not answered';
  }
  return question.isCorrect ? 'Correct' : 'Incorrect';
};

/**
 * What the student chose, as they would recognise it rather than as it is
 * stored. Falls back to the stored value if the option has since gone.
 */
export const chosenText = (question: ReviewQuestion): string => {
  if (!question.answered) {
    return '';
  }
  const value = first(question.selectedAnswer);
  return optionForValue(question.question?.answer, value)?.answer ?? value;
};

export const correctText = (question: ReviewQuestion): string => {
  const value = first(question.correctAnswer);
  return optionForValue(question.question?.answer, value)?.answer ?? value;
};

/** Only the questions that cost marks, which is what most students reread. */
export const onlyMissed = (questions: ReviewQuestion[]): ReviewQuestion[] =>
  (questions ?? []).filter((question) => !question.isCorrect);

export const missedCount = (questions: ReviewQuestion[]): number => onlyMissed(questions).length;
